/* eslint-disable react/prop-types */
import React, { useMemo } from 'react';
import { Dropdown, Icon, Radio, Checkbox, Input } from 'tea-component';
import { RegionSelectButtonContextType, RegionSelectPropsType } from './type';
import RegionSelectButton from './RegionSelectButton';
import { TAB_COMMON, TAB_POPULAR, SELECTED_OPTION_RADIO, LOCALE_CN } from './config';
import {
  getText,
  getBubbleContentText,
  getLocaleText,
} from './util';
import useData from './hooks';
import useLocae from './useLocale';
import './index.scss';
import classNames from 'classnames';
import Abnormal from '@/pages/home/components/Abnormal';
import { isEmpty } from 'lodash-es';
import { List as VirtualizedList } from 'react-virtualized';
import CommonBubble from '../CommonBubble';
import ExactOverflowMarketItem from './ExactOverflowMarketItem';
import ExactOverflowRegionItem from './ExactOverflowRegionItem';
import ExactOverflowSearchTagText from './ExactOverflowSearchTagText';
import MarketGroupHeaderItem from './MarketGroupHeaderItem';
import MixedTypeIcon from '@/pages/home/components/MixedTypeIcon';

const NewRegionSelect: React.FC<RegionSelectPropsType> = (props) => {
  const {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    value = null, // 值
    radioOptions = [], // 单选项
    options = [], // 多选项
    popularList = [], // 热门选项
    button = null,
    className,
    boxClassName = '',
    locale = LOCALE_CN,
    language,
    isTopCompanies = false,
    isNotRegionIcon = false,  // 是否非国家地区图片
    selectFilterType = 'region',
    showRadioOptionsTips = true,
    radioOptionsTips = {},
    loading = false,
    error = false,
    leftBubblePlacement,
    leftBubblePlacementOffset,
    rightBubblePlacement,
    rightBubblePlacementOffset,
    shortcutOptions,
    tagNumber = 0,
    displayDisabledStatusOfSelected,
    header,
    contentHeader,
    contentLeftFooter,
    contentRightFooter,
    contentCenterFooter,
    renderRightContent,
    renderTag,
    getCustomRegionSelectAllButton,
    getCustomRegionClearAllButton,
    getCustomMarketSelectAllButton,
    getCustomMarketClearAllButton,
    placeholder,
    firstLevelTitle,
    resolveSecondLevelTitle,
    overflowBubbleProps,
    rightListWidth,
    rightListHeight,
    enableExactOverflowBubble = false,
    enableSearchTagOverflowBubble = false,
    ...restProps
  } = props;

  const listBubbleProps = overflowBubbleProps?.listRow;
  const searchTagBubbleProps = overflowBubbleProps?.searchTag;

  const {
    searchInputRef,
    valueRef,
    search,
    setSearch,
    currentTab,
    selectedRegion,
    selectedRegionName,
    selectedRegionItem,
    allChildren,
    realOptionsAndChildren,
    realMarketsList,
    indeterminateRegions,
    selectAllInfo,
    selectedType,
    selectedMarketsInfo,
    handleClickTab,
    handleChangeRadio,
    handleClickRegion,
    // handleClickGlobalDiv,
    handleClickRegionCheckBox,
    handleChangeMarkets,
    handleClickRegionSelectAll,
    handleClickRegionClearAll,
    handleClickMarketSelectAll,
    handleClickMarketClearAll,
    handleCloseTag,
    handleClickSearchClose,
    handleClickSearchWrap,
    handleCloseRegionSelect,
    shortcutActive,
    handleClickShortcut,
    realRegionsList
  } = useData(props);

  const { formatMessage } = useLocae(locale, language);

  const { markets = [] } = valueRef.current || {};
  const { regionSelectAll, marketsSelectAll } = selectAllInfo || {};
  const { realOptions } = realOptionsAndChildren || {};
  const radioValue = selectedType.current === SELECTED_OPTION_RADIO ? markets?.[0] : undefined;
  const isDataUnder = useMemo(
    () =>
      (radioValue === 'global' || radioValue === 'global_wo_china') &&
      !getLocaleText(selectedRegionName, locale),
    [radioValue, selectedRegionName, locale],
  );

  const globalAndGlobalWOChinaTips = useMemo<string>(() => {
    if (!showRadioOptionsTips) return '';
    if (isTopCompanies) return formatMessage({ id: 'global_global_wo_china_tips_of_top_companies' });
    if (isDataUnder) {
      // 自定义文案
      if (radioOptionsTips?.[radioValue || '']) {
        return radioOptionsTips?.[radioValue || ''];
      }
      if (radioValue === 'global') return formatMessage({ id: 'aggregated_data_under_global' });
      if (radioValue === 'global_wo_china') return formatMessage({ id: 'aggregated_data_under_global_except' });
    }
    return formatMessage({ id: 'country_no_data' });
  }, [isTopCompanies, isDataUnder, formatMessage, currentTab, radioValue, showRadioOptionsTips, radioOptionsTips]);
  const imgInvert = ['Steam'];

  const highlightText = (text: string, search: string) => {
    if (!search) return text;
    const escapedSearch = search.replace(/[-/\\^$*+?.()|[\]{}]/g, '\\$&');
    const regex = new RegExp(`(${escapedSearch})`, 'gi'); // Case-insensitive regex
    const parts = text.split(regex);
    return (
      <>
        {parts.map((part, index) =>
          regex.test(part) ? (
            <span key={index} style={{ color: "#326ff0" }}>{part}</span>
          ) : (
            part
          )
        )}
      </>
    );
  };

  // 渲染自定义按钮的辅助函数
  const renderCustomButton = (
    getCustomButton: undefined | ((context: RegionSelectButtonContextType) => JSX.Element | React.ReactNode),
    context: any,
    defaultOnClick: () => void,
    defaultText: string
  ) => {
    if (getCustomButton) {
      return getCustomButton({...context, defaultOnClick, classname: 'dbc-region-select-button', defaultText}); 
    }
    return (
      <span
        className="dbc-region-select-button"
        onClick={defaultOnClick}
      >
        {defaultText}
      </span>
    );
  };

  const { slicedSelectedMarket, restSelectedMarketLength } = useMemo(() => {
    if (!tagNumber || selectedMarketsInfo?.length <= tagNumber) {
      return {
        slicedSelectedMarket: selectedMarketsInfo,
        restSelectedMarketLength: 0,
      };
    }
    const slicedSelectedMarket = selectedMarketsInfo?.slice(0, tagNumber);
    const restSelectedMarketLength = selectedMarketsInfo?.length - tagNumber;
    return {
      slicedSelectedMarket,
      restSelectedMarketLength,
    };
  }, [tagNumber, selectedMarketsInfo]);

  // 自定义右侧内容：返回非空时替代默认的二级checkbox列表
  const customRight = useMemo(()=> {
    return selectedRegionItem && renderRightContent?.(
      selectedRegionItem,
      valueRef.current || { type: '', regions: [], markets: [] }
    );
  }, [selectedRegionItem, renderRightContent, valueRef.current]);

  const secondLevelDisplayTitle = resolveSecondLevelTitle
    ? resolveSecondLevelTitle(selectedRegionItem, locale)
    : getLocaleText(selectedRegionName, locale);

  return (
    <>
      <Dropdown
        button={
          button || (
            <RegionSelectButton
              value={valueRef.current}
              options={options}
              selectFilterType={selectFilterType}
              allChildren={allChildren}
              radioOptions={radioOptions}
              formatMessage={formatMessage}
              locale={locale}
              placeholder={placeholder}
            />
          )
        }
        className={classNames('dbo-region-select', className)}
        size="m"
        appearance="button"
        clickClose={false}
        closeOnScroll={true}
        onClose={handleCloseRegionSelect}
        boxClassName={classNames('dbc-region-select-container', boxClassName, {
          'dbc-region-select--list-bubble-ellipsis': enableExactOverflowBubble,
          'dbc-region-select--search-tag-bubble-ellipsis': enableSearchTagOverflowBubble,
        })}
        overlayClassName='dbc-region-select-box'
        {...restProps}
      >
        {() => (
          <>
            {header}
            {/* search */}
            <div className="dbc-region-select-search-container">
              <div className="dbc-region-select-search-wrap">
                <div className="dbc-region-select-search-input-wrap">
                  <div
                    className="dbc-region-select-search-input-wrap-inner"
                    onClick={handleClickSearchWrap}
                  >
                    {slicedSelectedMarket?.map((item) => {
                      const tagContent = renderTag || item?.renderTag;
                      const displayText = tagContent?.(item, locale) || getText(item, locale);
                      const rawText = enableSearchTagOverflowBubble
                        ? (getText(item, locale) || '')
                        : '';
                      return (
                        <div
                          className={classNames("tea-tag dbc-region-select-tag",{"dbc-region-select-tag-disabled": displayDisabledStatusOfSelected && item?.disabled})}
                          key={item?.value}
                        >
                          {enableSearchTagOverflowBubble ? (
                            <ExactOverflowSearchTagText
                              item={item}
                              locale={locale}
                              rawText={rawText}
                              bubbleProps={searchTagBubbleProps}
                            >
                              {displayText}
                            </ExactOverflowSearchTagText>
                          ) : (
                            <span>{displayText}</span>
                          )}
                          <Icon
                            onClick={() => {
                              if (displayDisabledStatusOfSelected && item?.disabled) return;
                              handleCloseTag(item?.value);
                            }}
                            type="close"
                          />
                        </div>
                      );
                    })}
                    {
                      restSelectedMarketLength > 0 && (
                        <div
                          className="tea-tag dbc-region-select-tag"
                          key={`rest-${restSelectedMarketLength}`}
                        >
                          <span style={{ maxWidth: 'unset' }}>{`+${restSelectedMarketLength}`}</span>
                        </div>
                      )
                    }
                    <Input
                      placeholder={formatMessage({ id: 'search' })}
                      value={search}
                      ref={searchInputRef}
                      className="dbc-region-select-search-input"
                      type="text"
                      onChange={(val) => {
                        setSearch(val);
                      }}
                    />
                  </div>
                  <div className="dbc-region-select-search-icon">
                    {selectedMarketsInfo.length > 0 || search ? (
                      <Icon type="dismiss" onClick={handleClickSearchClose} />
                    ) : null}
                    <Icon type="search" />
                    {/* <Icon type="dismiss" />*/}
                  </div>
                </div>
              </div>
            </div>

            {
              <Abnormal
                isLoading={loading}
                isError={error}
                component={
                  <>
                    {contentHeader}
                    {/* Tab */}
                    {popularList.length > 1 && (
                      <div className="dbc-region-select-tab-wrap">
                        <div
                          onClick={() => {
                            handleClickTab(TAB_COMMON);
                          }}
                          className={`dbc-region-select-tab-item ${currentTab === TAB_COMMON ? 'active' : ''
                          }`}
                        >
                          {formatMessage({ id: 'common' })}
                        </div>
                        <div
                          onClick={() => {
                            handleClickTab(TAB_POPULAR);
                          }}
                          className={`dbc-region-select-tab-item ${currentTab === TAB_POPULAR ? 'active' : ''
                          }`}
                        >
                          {formatMessage({ id: 'popular' })}
                        </div>
                      </div>
                    )}

                    {/* Content */}
                    <div className="dbc-region-select-content-container">
                      {/* {
                currentTab === TAB_POPULAR ?
              } */}
                      {/* 左侧选择区域 */}
                      {currentTab === TAB_COMMON && (
                        <div className="dbc-region-select-content-wrap">
                          {/* 快捷栏数据 */}
                          {
                            !isEmpty(shortcutOptions) && !search && <>
                              <div className="dbc-region-select-content-title-wrap">
                                <span>{formatMessage({ id: 'shortcut_title' })}</span>
                              </div>
                              <div className="dbc-region-select-content-shortcut">
                                {
                                  shortcutOptions?.map((task: any) => (
                                    <div
                                      onClick={() => handleClickShortcut(task?.type)}
                                      className={classNames('dbc-region-select-content-shortcut-item', {
                                        'dbc-region-select-content-shortcut-item-active': task?.type === shortcutActive,
                                        'dbc-region-select-content-shortcut-item-disabled': task?.disabled,
                                      })}
                                      key={task?.type}>
                                      {getText(task, locale)}
                                    </div>
                                  ))
                                }
                              </div>
                            </>
                          }
                          {/* Radios */}
                          {radioOptions.length > 0 && !search && (
                            <>
                              <div className="dbc-region-select-content-title-wrap">
                                <span>{formatMessage({ id: 'radio_title' })}</span>
                              </div>
                              <div className="dbc-region-select-item-wrap mb4">
                                <Radio.Group
                                  className="dbc-region-select-radio"
                                  value={radioValue}
                                  onChange={handleChangeRadio}
                                >
                                  {radioOptions?.map((item) => {
                                    const { value, render, infoIcon, disabled } = item;
                                    return (
                                      <div
                                        key={item.value}
                                        className="dbc-region-select-radio-info-icon"
                                      // onClick={(e) => {
                                      //   handleClickGlobalDiv(e, item);
                                      // }}
                                      >
                                        <Radio name={value} disabled={disabled}>
                                          {render || getText(item, locale)}
                                        </Radio>
                                        {infoIcon && infoIcon}
                                      </div>
                                    );
                                  })}
                                </Radio.Group>
                              </div>
                            </>
                          )}

                          {/* Checkbox */}
                          <>
                            <div className="dbc-region-select-content-title-wrap">
                              <span>{firstLevelTitle || formatMessage({ id: 'region_title' })}</span>
                              {realOptions.length > 0 &&
                                (regionSelectAll ? (
                                  renderCustomButton(
                                    getCustomRegionClearAllButton,
                                    {
                                      isAllSelected: regionSelectAll,
                                      totalCount: realOptions.length,
                                      selectedCount: realRegionsList.length,
                                      disabledCount: realOptions.filter(item => item.disabled).length,
                                      onClick: () => handleClickRegionClearAll(),
                                      locale,
                                      formatMessage
                                    },
                                    () => handleClickRegionClearAll(),
                                    formatMessage({ id: 'clear_all' })
                                  )
                                ) : (
                                  renderCustomButton(
                                    getCustomRegionSelectAllButton,
                                    {
                                      isAllSelected: regionSelectAll,
                                      totalCount: realOptions.length,
                                      selectedCount: realRegionsList.length,
                                      disabledCount: realOptions.filter(item => item.disabled).length,
                                      onClick: handleClickRegionSelectAll,
                                      locale,
                                      formatMessage
                                    },
                                    handleClickRegionSelectAll,
                                    formatMessage({ id: 'select_all' })
                                  )
                                ))}
                            </div>
                            {realOptions.length > 0 ? (
                              <div className="dbc-region-select-item-wrap">
                                {realOptions.map((item) => {
                                  const { value, render, children = [], disabled, icon } = item;
                                  const isIndeterminate = indeterminateRegions.includes(value);
                                  const rawText = getText(item, locale) || '';
                                  const title = highlightText(rawText, search);
                                  if (enableExactOverflowBubble) {
                                    return (
                                      <ExactOverflowRegionItem
                                        key={item.value}
                                        item={item}
                                        locale={locale}
                                        title={title}
                                        rawText={rawText}
                                        childrenCount={children.length}
                                        isNotRegionIcon={isNotRegionIcon}
                                        selectedRegion={selectedRegion}
                                        isIndeterminate={isIndeterminate}
                                        isChecked={realRegionsList.includes(value)}
                                        leftBubblePlacement={leftBubblePlacement}
                                        leftBubblePlacementOffset={leftBubblePlacementOffset}
                                        listBubbleProps={listBubbleProps}
                                        onClickRegionCheckBox={() => handleClickRegionCheckBox(item)}
                                        onClickRegion={(e) => handleClickRegion(e as React.MouseEvent<HTMLDivElement>, item)}
                                      />
                                    );
                                  }
                                  return (
                                    <CommonBubble key={item.value} placement={leftBubblePlacement} placementOffset={leftBubblePlacementOffset} dark arrowPointAtCenter closeOnScroll content={getBubbleContentText(item, locale)}>
                                      <Checkbox
                                        key={value}
                                        className={classNames('dbc-region-select-checkbox', {
                                          'active': value === selectedRegion
                                        })}
                                        onClick={() => {
                                          handleClickRegionCheckBox(item);
                                        }}
                                        display="block"
                                        disabled={disabled}
                                        indeterminate={isIndeterminate}
                                        value={isIndeterminate ? undefined : realRegionsList.includes(value)}
                                      >
                                        {icon && <img className={`dbc-region-select-item-icon ${isNotRegionIcon ? 'device-icon' : ''}`} src={icon} alt="" />}
                                        <div
                                          className="dbc-region-select-checkbox-name-wrap"
                                          onClick={(e) => {
                                            handleClickRegion(e, item);
                                          }}
                                        >
                                          <span className="dbc-region-select-checkbox-name">
                                            {render || title}{!item?.hideChildrenCount && <>&nbsp;({children.length})</>}
                                          </span>
                                          <Icon type="arrowright" />
                                        </div>
                                      </Checkbox>
                                    </CommonBubble>
                                  );
                                })}
                              </div>
                            ) : (
                              <div className="dbc-region-select-item-none">
                                {formatMessage({ id: 'country_no_data' })}
                              </div>
                            )}
                          </>
                          { realOptions.length > 0 && contentLeftFooter?.(selectedRegionItem, locale)}
                        </div>
                      )}

                      {/* 右侧选择区域 */}
                      <div className="dbc-region-select-content-wrap">
                        {
                          customRight ? customRight : <>
                            <div className="dbc-region-select-content-title-wrap">
                              {currentTab === TAB_COMMON ?
                                <span className="dbc-region-select-content-country-title">
                                  {secondLevelDisplayTitle}
                                </span> :
                                <span>{formatMessage({ id: 'popular' })}</span>
                              }
                              {realMarketsList.length > 0 &&
                              (marketsSelectAll ? (
                                renderCustomButton(
                                  getCustomMarketClearAllButton,
                                  {
                                    isAllSelected: marketsSelectAll,
                                    totalCount: realMarketsList.length,
                                    selectedCount: markets.length,
                                    disabledCount: realMarketsList.filter(item => item.disabled && !item.isGroupHeader).length,
                                    onClick: handleClickMarketClearAll,
                                    locale,
                                    formatMessage
                                  },
                                  handleClickMarketClearAll,
                                  formatMessage({ id: 'clear_all' })
                                )
                              ) : (
                                renderCustomButton(
                                  getCustomMarketSelectAllButton,
                                  {
                                    isAllSelected: marketsSelectAll,
                                    totalCount: realMarketsList.length,
                                    selectedCount: markets.length,
                                    disabledCount: realMarketsList.filter(item => item.disabled && !item.isGroupHeader).length,
                                    onClick: handleClickMarketSelectAll,
                                    locale,
                                    formatMessage
                                  },
                                  handleClickMarketSelectAll,
                                  formatMessage({ id: 'select_all' })
                                )
                              ))}
                            </div>
                            {realMarketsList.length > 0 ? (
                              <div className="dbc-region-select-item-wrap">
                                <Checkbox.Group
                                  className="dbc-region-select-checkbox"
                                  value={markets}
                                  onChange={handleChangeMarkets}
                                  style={{}}
                                >
                                  <VirtualizedList
                                    rowCount={realMarketsList?.length}
                                    rowRenderer={({ index, style }: { index: number; style: React.CSSProperties; }) => {
                                      const item = realMarketsList[index];
                                      // 列表刷新与虚拟行 index 可能短暂不同步，缺项时不调用 getText 等，避免传入 undefined
                                      if (!item) {
                                        return <div style={style} key={index} />;
                                      }
                                      const { value, icon, svgIconType, render, disabled, isGroupHeader } = item;
                                      const rawText = getText(item, locale) || '';
                                      const title = highlightText(rawText, search);
                                      if (isGroupHeader) {
                                        return (
                                          <div style={style} key={index}>
                                            <MarketGroupHeaderItem
                                              icon={icon}
                                              svgIconType={svgIconType}
                                              render={render}
                                              title={title}
                                              rawText={rawText}
                                              isNotRegionIcon={isNotRegionIcon}
                                              imgInvert={imgInvert}
                                            />
                                          </div>
                                        );
                                      }
                                      return (
                                        <div style={style} key={index}>
                                          {enableExactOverflowBubble ? (
                                            <ExactOverflowMarketItem
                                              item={item}
                                              locale={locale}
                                              title={title}
                                              rawText={rawText}
                                              isNotRegionIcon={isNotRegionIcon}
                                              imgInvert={imgInvert}
                                              rightBubblePlacement={rightBubblePlacement}
                                              rightBubblePlacementOffset={rightBubblePlacementOffset}
                                              listBubbleProps={listBubbleProps}
                                            />
                                          ) : (
                                            <CommonBubble placement={rightBubblePlacement} placementOffset={rightBubblePlacementOffset} dark arrowPointAtCenter closeOnScroll content={getBubbleContentText(item, locale)}>
                                              <Checkbox key={value} name={value} disabled={disabled}>
                                                {render || (
                                                  <>
                                                    <MixedTypeIcon
                                                      icon={icon}
                                                      svgIconType={svgIconType}
                                                      rawText={rawText}
                                                      imgInvert={imgInvert}
                                                      className={classNames('dbc-region-select-item-icon', {
                                                        'device-icon': isNotRegionIcon,
                                                      })}
                                                    />
                                                    <span>{title}</span>
                                                  </>
                                                )}
                                              </Checkbox>
                                            </CommonBubble>
                                          )}
                                        </div>
                                      );
                                    }}
                                    width={rightListWidth ?? 258}
                                    height={rightListHeight ?? 305}
                                    rowHeight={36}
                                    overscanRowCount={5}
                                    scrollToIndex={0}
                                  />
                                </Checkbox.Group>
                              </div>
                            ) : (
                              <div className={`dbc-region-select-item-none ${isDataUnder || isTopCompanies ? 'data-under' : ''}`} dangerouslySetInnerHTML={{__html: globalAndGlobalWOChinaTips}}>
                                {/* {isDataUnder ? formatMessage({ id: 'aggregated_data_under' }) : formatMessage({ id: 'country_no_data' })} */}
                                {/* {globalAndGlobalWOChinaTips}*/}
                              </div>
                            )}
                          </>
                        }
                        {/* 待确认: 自定义组件是否需要和内部数据源绑定 */}
                        { realMarketsList.length > 0 && contentRightFooter }
                      </div>
                    </div>
                    {contentCenterFooter}
                  </>
                }

              />
            }

          </>
        )}
      </Dropdown>
    </>
  );
};

export default NewRegionSelect;